import React, { useState, useEffect } from 'react';

import { 
  getAllTables, 
  addTable, 
  editTable, 
  deleteTable, 
  updateTableStatus 
} from '../Services/TablesApi';
import { useOrganisations } from '../Auth/useOrganisations';
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';

const TablesInterface = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [organisations, setOrganisations, usedOrganisations, setUsedOrganisations] = useOrganisations();

  // Form state
  const [formData, setFormData] = useState({
    table_number: '',
    capacity: 2,
    status: 'available',
    location: 'indoor'
  });

  // Load tables
  const loadTables = async () => {
    try {
      setLoading(true);
      const response = await getAllTables(usedOrganisations);
      if (response.data.success !== "0") {
        setTables(response.data.data);
      }
    } catch (error) {
      console.error('Error loading tables:', error);
      alert('Erreur lors du chargement des tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
  }, [usedOrganisations]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Open dialog for adding/editing
  const handleOpenDialog = (table = null) => {
    if (table) {
      setEditingTable(table);
      setFormData({
        table_number: table.table_number,
        capacity: table.capacity,
        status: table.status,
        location: table.location
      });
    } else {
      setEditingTable(null);
      setFormData({
        table_number: '',
        capacity: 2,
        status: 'available',
        location: 'indoor'
      });
    }
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTable(null);
  };

  // Save table (add or edit)
  const handleSaveTable = async () => {
    try {
      if (editingTable) {
        await editTable(
          formData.table_number,
          formData.capacity,
          formData.status,
          formData.location,
          editingTable._id
        );
      } else {
        await addTable(
          formData.table_number,
          formData.capacity,
          formData.status,
          formData.location,
          usedOrganisations
        );
      }
      await loadTables();
      handleCloseDialog();
      alert(`Table ${editingTable ? 'modifiée' : 'ajoutée'} avec succès!`);
    } catch (error) {
      console.error('Error saving table:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  // Delete table
  const handleDeleteTable = async (tableId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette table?')) {
      try {
        await deleteTable(tableId);
        await loadTables();
        alert('Table supprimée avec succès!');
      } catch (error) {
        console.error('Error deleting table:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  // Update table status
  const handleStatusChange = async (tableId, newStatus) => {
    try {
      await updateTableStatus(tableId, newStatus);
      await loadTables();
      alert('Statut mis à jour!');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'occupied': return 'error';
      case 'reserved': return 'warning';
      case 'cleaning': return 'info';
      default: return 'default';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'occupied': return 'Occupée';
      case 'reserved': return 'Réservée';
      case 'cleaning': return 'Nettoyage';
      default: return status;
    }
  };

  // Get location text
  const getLocationText = (location) => {
    switch (location) {
      case 'indoor': return 'Intérieur';
      case 'outdoor': return 'Terrasse';
      case 'vip': return 'VIP';
      default: return location;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement des tables...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Gestion des Tables
        </Typography>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#dc3545',
            '&:hover': {
              backgroundColor: '#c82333',
            },
          }}
        >
          Ajouter une Table
        </Button>
      </Box>

      {/* Statistics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 120, backgroundColor: '#e8f5e8' }}>
          <CardContent>
            <Typography color="text.secondary">Disponibles</Typography>
            <Typography variant="h6" color="success.main">
              {tables.filter(t => t.status === 'available').length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 120, backgroundColor: '#ffeaea' }}>
          <CardContent>
            <Typography color="text.secondary">Occupées</Typography>
            <Typography variant="h6" color="error.main">
              {tables.filter(t => t.status === 'occupied').length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 120, backgroundColor: '#fff4e5' }}>
          <CardContent>
            <Typography color="text.secondary">Réservées</Typography>
            <Typography variant="h6" color="warning.main">
              {tables.filter(t => t.status === 'reserved').length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 120, backgroundColor: '#e3f2fd' }}>
          <CardContent>
            <Typography color="text.secondary">Nettoyage</Typography>
            <Typography variant="h6" color="info.main">
              {tables.filter(t => t.status === 'cleaning').length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tables Grid */}
      <Grid container spacing={3}>
        {tables.map((table) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={table._id}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
                border: table.status === 'occupied' ? '2px solid #f44336' : 
                        table.status === 'reserved' ? '2px solid #ff9800' : 'none'
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                {/* Table Number */}
                <Typography variant="h4" component="div" fontWeight="bold" color="primary" gutterBottom>
                  Table {table.table_number}
                </Typography>

                {/* Status Chip */}
                <Chip
                  label={getStatusText(table.status)}
                  color={getStatusColor(table.status)}
                  sx={{ mb: 2 }}
                />

                {/* Capacity */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Typography variant="body1">
                    {table.capacity} {table.capacity > 1 ? 'personnes' : 'personne'}
                  </Typography>
                </Box>

                {/* Location */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getLocationText(table.location)}
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Tooltip title="Modifier">
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpenDialog(table)}
                      size="small"
                    >
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Supprimer">
                    <IconButton 
                      color="error" 
                      onClick={() => handleDeleteTable(table._id)}
                      size="small"
                    >
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Status Change Buttons */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2, justifyContent: 'center' }}>
                  {['available', 'occupied', 'reserved', 'cleaning'].map((status) => (
                    <Chip
                      key={status}
                      label={getStatusText(status).charAt(0)}
                      color={getStatusColor(status)}
                      variant={table.status === status ? 'filled' : 'outlined'}
                      size="small"
                      onClick={() => handleStatusChange(table._id, status)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTable ? 'Modifier la Table' : 'Ajouter une Table'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Numéro de Table"
              name="table_number"
              value={formData.table_number}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Capacité"
              name="capacity"
              type="number"
              value={formData.capacity}
              onChange={handleInputChange}
              fullWidth
              required
              inputProps={{ min: 1, max: 20 }}
            />
            <TextField
              label="Statut"
              name="status"
              select
              value={formData.status}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem value="available">Disponible</MenuItem>
              <MenuItem value="occupied">Occupée</MenuItem>
              <MenuItem value="reserved">Réservée</MenuItem>
              <MenuItem value="cleaning">Nettoyage</MenuItem>
            </TextField>
            <TextField
              label="Emplacement"
              name="location"
              select
              value={formData.location}
              onChange={handleInputChange}
              fullWidth
            >
              <MenuItem value="indoor">Intérieur</MenuItem>
              <MenuItem value="outdoor">Terrasse</MenuItem>
              <MenuItem value="vip">VIP</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSaveTable} 
            variant="contained"
            disabled={!formData.table_number}
          >
            {editingTable ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Empty State */}
      {tables.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Aucune table configurée
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Commencez par ajouter votre première table
          </Typography>
          <Button
            variant="contained"
            onClick={() => handleOpenDialog()}
          >
            Ajouter une Table
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TablesInterface;